import { useEffect, useRef } from "react";

/**
 * Periodically checks whether a newer frontend build has been deployed and
 * invokes `onNewVersion` once when it detects one.
 *
 * Strategy (no build/pipeline changes required): it fetches the deployed
 * `index.html` and derives a signature from the hashed asset paths it
 * references (e.g. `/static/js/main.[hash].js`). Those hashes change on every
 * production build, so a difference between the signature captured at load time
 * and a later poll means a new version is live on S3.
 *
 * If you later add an explicit metadata file (e.g. `public/version.json`
 * stamped with the build id in CI), just point `META_URL` at it and adjust
 * `extractSignature` to read the field — the rest keeps working.
 */

const DEFAULT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
// The app is served from the domain root (no `homepage`, rsbuild assetPrefix
// "/"), so index.html lives at "/". Avoid `process.env.*` here: rsbuild only
// statically replaces an allowlist of vars and would leave a bare `process`
// reference that throws "process is not defined" in the browser.
const META_URL = "/index.html";

/** Build a stable signature from the assets referenced by index.html. */
function extractSignature(html: string): string {
    const matches = html.match(/\/static\/[^\s"')]+\.(?:js|css)/g);
    if (matches && matches.length > 0) {
        return Array.from(new Set(matches)).sort().join("|");
    }
    // Fallback when no hashed assets are found: use the raw markup length.
    return `len:${html.length}`;
}

export function useVersionPolling(
    onNewVersion: () => void,
    intervalMs: number = DEFAULT_INTERVAL_MS
): void {
    // Keep the latest callback without re-subscribing the effect.
    const callbackRef = useRef(onNewVersion);
    callbackRef.current = onNewVersion;

    useEffect(() => {
        let cancelled = false;
        let notified = false;
        let baseline: string | null = null;

        const check = async () => {
            if (notified || cancelled) return;
            try {
                const res = await fetch(`${META_URL}?_=${Date.now()}`, {
                    cache: "no-store",
                    headers: { "Cache-Control": "no-cache" },
                });
                if (!res.ok || cancelled) return;

                const signature = extractSignature(await res.text());
                if (cancelled) return;

                if (baseline === null) {
                    baseline = signature;
                    return;
                }
                if (signature !== baseline) {
                    notified = true;
                    callbackRef.current();
                }
            } catch {
                // Network blips are ignored; the next tick will retry.
            }
        };

        // Establish the baseline now, then poll on an interval.
        void check();
        const intervalId = window.setInterval(() => void check(), intervalMs);

        // Also re-check when the user comes back to the tab, so a long-idle
        // tab gets the heads-up promptly instead of waiting a full interval.
        const onVisible = () => {
            if (document.visibilityState === "visible") void check();
        };
        const onFocus = () => void check();
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onFocus);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onFocus);
        };
    }, [intervalMs]);
}

export default useVersionPolling;
